import React, { useState } from 'react';
import { Bell, User, Building, Menu, PanelLeft, LogOut, Copy, Check } from 'lucide-react';
import { useOrganization } from '../OrganizationContext';
import { useAuth } from '../AuthContext';
import AnimatedDropdown from './AnimatedDropdown';
import { ThemeToggle } from './ThemeToggle';

const DashboardHeader = ({ onMenuClick, onSidebarToggle, sidebarCollapsed, onLogout }) => {
  const { organizations, currentOrg, switchOrganization, loading } = useOrganization();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [orgIdCopied, setOrgIdCopied] = useState(false);

  const handleCopyOrgId = async () => {
    if (!currentOrg?.id) return;
    try {
      await navigator.clipboard.writeText(currentOrg.id);
      setOrgIdCopied(true);
      setTimeout(() => setOrgIdCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy organization ID:', error);
    }
  };

  const handleOrgSwitch = async (orgId) => {
    if (orgId === currentOrg?.id) return; // Don't switch if already selected
    if (!orgId) {
      console.error('No organization ID provided');
      return;
    }
    try {
      await switchOrganization(orgId);
      // switchOrganization will reload the page, so we don't need to do anything here
    } catch (error) {
      console.error('Failed to switch organization:', error);
      // Show error to user
      alert(`Failed to switch organization: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    }
  };

  return (
    <header className={`h-14 border-b border-border bg-card/70 backdrop-blur-sm fixed top-0 z-50 transition-all duration-300 ${sidebarCollapsed ? 'lg:left-20' : 'lg:left-64'} left-0 right-0`}>
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="p-2 hover:bg-muted rounded-lg transition-colors lg:hidden"
          >
            <Menu className="w-5 h-5 text-foreground" />
          </button>

          {/* Sidebar Toggle Button (Desktop) */}
          <button
            onClick={onSidebarToggle}
            className="hidden lg:flex p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
          >
            <PanelLeft className="w-5 h-5" />
          </button>

          {/* Organization Selector */}
          <div className="flex items-center gap-3">
            <Building className="w-5 h-5 text-primary" />
            <AnimatedDropdown
              value={currentOrg?.id || ''}
              onChange={(value) => {
                handleOrgSwitch(value);
              }}
              disabled={loading || organizations.length === 0}
              options={loading ? [] : organizations.map(org => ({ value: org.id, label: org.name }))}
              placeholder={loading ? 'Loading...' : organizations.length === 0 ? 'No organizations' : 'Select organization'}
              className="w-48"
              icon={Building}
            />
            <button
              onClick={handleCopyOrgId}
              disabled={!currentOrg?.id}
              className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={currentOrg?.id ? `Copy organization ID (${currentOrg.id})` : 'No organization selected'}
            >
              {orgIdCopied ? (
                <Check className="w-4 h-4 text-primary" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full animate-pulse" />
          </button>

          {/* User Profile & Logout */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-button flex items-center justify-center relative">
                <User className="w-4 h-4 text-primary-foreground" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-card" />
              </div>
              <div className="hidden md:block text-sm">
                <div className="font-semibold text-foreground">
                  {user?.name || user?.email || 'User'}
                </div>
                <div className="text-muted-foreground text-xs">
                  {user?.email ? user.email : (user?.user_type === 'developer' ? 'Developer' : 'Admin')}
                </div>
              </div>
            </div>
            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground group"
              title="Logout"
            >
              <LogOut className="w-5 h-5 group-hover:text-primary" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;

